import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  UserPlus, 
  Briefcase, 
  Calendar, 
  ChatCircle, 
  Crown, 
  Shield, 
  User,
  Trash,
  PencilSimple,
  Plus,
  X,
  CaretDown,
  CaretUp,
  SpinnerGap,
  EnvelopeSimple,
  Clock,
  MapPin,
  Check,
  Warning,
  ArrowRight,
  Circle
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { teamsAPI, jobsAPI } from '../api/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const TeamManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('members');
  const [myInvitations, setMyInvitations] = useState([]);
  
  // Modals
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [showInviteMember, setShowInviteMember] = useState(false);
  const [showAssignJob, setShowAssignJob] = useState(false);
  const [showScheduleEntry, setShowScheduleEntry] = useState(false);
  
  // Form data
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamDesc, setNewTeamDesc] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteMessage, setInviteMessage] = useState('');
  
  // Assignments & Schedule
  const [assignments, setAssignments] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [assignmentNotes, setAssignmentNotes] = useState('');
  
  // Schedule form
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleStartTime, setScheduleStartTime] = useState('09:00');
  const [scheduleEndTime, setScheduleEndTime] = useState('17:00');
  const [scheduleNotes, setScheduleNotes] = useState('');
  const [scheduleJobId, setScheduleJobId] = useState('');
  
  // Chat
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [onlineMembers, setOnlineMembers] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const chatContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // WebSocket connection for real-time chat
  const connectWebSocket = useCallback((teamId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    
    const token = localStorage.getItem('token');
    if (!token || !teamId) return;
    
    const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/teams/${teamId}/ws?token=${token}`);
    
    ws.onopen = () => {
      console.log('Team chat WebSocket connected');
      setWsConnected(true);
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        switch (data.type) {
          case 'team_chat':
            // New message received
            setMessages(prev => [...prev, data.message]);
            // Scroll to bottom
            setTimeout(() => {
              if (chatContainerRef.current) {
                chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
              }
            }, 100);
            break;
            
          case 'online_members':
            setOnlineMembers(data.members || []);
            break;
            
          case 'user_joined':
            setOnlineMembers(prev => {
              if (!prev.includes(data.user_id)) {
                return [...prev, data.user_id];
              }
              return prev;
            });
            break;
            
          case 'user_left':
            setOnlineMembers(prev => prev.filter(id => id !== data.user_id));
            break;
            
          case 'typing':
            if (data.is_typing) {
              setTypingUsers(prev => {
                if (!prev.find(u => u.user_id === data.user_id)) {
                  return [...prev, { user_id: data.user_id, user_name: data.user_name }];
                }
                return prev;
              });
            } else {
              setTypingUsers(prev => prev.filter(u => u.user_id !== data.user_id));
            }
            break;
            
          default:
            break;
        }
      } catch (e) {
        console.error('Error parsing WebSocket message:', e);
      }
    };
    
    ws.onclose = () => {
      console.log('Team chat WebSocket disconnected');
      setWsConnected(false);
      // Attempt to reconnect after 3 seconds
      setTimeout(() => {
        if (teamId) connectWebSocket(teamId);
      }, 3000);
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    wsRef.current = ws;
  }, []);

  // Disconnect WebSocket on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  // Connect to WebSocket when team is loaded and chat tab is active
  useEffect(() => {
    if (team && activeTab === 'chat') {
      connectWebSocket(team._id);
    }
  }, [team, activeTab, connectWebSocket]);

  const sendTypingIndicator = (isTyping) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        is_typing: isTyping
      }));
    }
  };

  const handleMessageInputChange = (e) => {
    setNewMessage(e.target.value);
    
    // Send typing indicator
    sendTypingIndicator(true);
    
    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Stop typing after 2 seconds of no input
    typingTimeoutRef.current = setTimeout(() => {
      sendTypingIndicator(false);
    }, 2000);
  };

  const fetchTeamData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get team info
      const teamRes = await teamsAPI.getMyTeam();
      if (teamRes.data.team) {
        setTeam(teamRes.data.team);
        
        // Get members
        const membersRes = await teamsAPI.getMembers(teamRes.data.team._id);
        setMembers(membersRes.data.members || []);
        
        // Get stats
        const statsRes = await teamsAPI.getTeamStats(teamRes.data.team._id);
        setStats(statsRes.data.stats);
        
        // Get assignments
        const assignRes = await teamsAPI.getAssignments(teamRes.data.team._id);
        setAssignments(assignRes.data.assignments || []);
        
        // Get schedule
        const schedRes = await teamsAPI.getSchedule(teamRes.data.team._id);
        setSchedule(schedRes.data.schedule || []);
        
        // Get messages
        const msgRes = await teamsAPI.getMessages(teamRes.data.team._id);
        setMessages(msgRes.data.messages || []);
      }
      
      // Get pending invitations for current user
      const invRes = await teamsAPI.getMyInvitations();
      setMyInvitations(invRes.data.invitations || []);
      
      // Get my jobs for assignment dropdown
      const jobsRes = await jobsAPI.getMyJobs({ limit: 50 });
      setMyJobs(jobsRes.data.jobs || []);
      
    } catch (error) {
      console.error('Error fetching team data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeamData();
  }, [fetchTeamData]);

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) {
      toast.error('Team name is required');
      return;
    }
    
    try {
      const res = await teamsAPI.createTeam({
        name: newTeamName,
        description: newTeamDesc
      });
      
      if (res.data.success) {
        toast.success('Team created!');
        setShowCreateTeam(false);
        setNewTeamName('');
        setNewTeamDesc('');
        fetchTeamData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create team');
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Email is required');
      return;
    }
    
    try {
      await teamsAPI.inviteMember(team._id, {
        email: inviteEmail,
        role: inviteRole,
        message: inviteMessage
      });
      
      toast.success('Invitation sent!');
      setShowInviteMember(false);
      setInviteEmail('');
      setInviteRole('member');
      setInviteMessage('');
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    }
  };

  const handleAcceptInvitation = async (invitationId) => {
    try {
      await teamsAPI.acceptInvitation(invitationId);
      toast.success('You have joined the team!');
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept invitation');
    }
  };

  const handleDeclineInvitation = async (invitationId) => {
    try {
      await teamsAPI.declineInvitation(invitationId);
      toast.success('Invitation declined');
      setMyInvitations(prev => prev.filter(i => i._id !== invitationId));
    } catch (error) {
      toast.error('Failed to decline invitation');
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (!window.confirm(`Remove ${memberName} from the team?`)) return;
    
    try {
      await teamsAPI.removeMember(team._id, memberId);
      toast.success('Member removed');
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleAssignJob = async () => {
    if (!selectedMember || !selectedJob) {
      toast.error('Select a member and job');
      return;
    }
    
    try {
      await teamsAPI.createAssignment(team._id, {
        member_id: selectedMember,
        job_id: selectedJob,
        notes: assignmentNotes
      });
      
      toast.success('Member assigned to job!');
      setShowAssignJob(false);
      setSelectedMember(null);
      setSelectedJob(null);
      setAssignmentNotes('');
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign job');
    }
  };

  const handleRemoveAssignment = async (assignmentId) => {
    try {
      await teamsAPI.removeAssignment(team._id, assignmentId);
      toast.success('Assignment removed');
      fetchTeamData();
    } catch (error) {
      toast.error('Failed to remove assignment');
    }
  };

  const handleAddScheduleEntry = async () => {
    if (!selectedMember || !scheduleDate) {
      toast.error('Select a member and date');
      return;
    }
    
    try {
      await teamsAPI.addScheduleEntry(team._id, {
        member_id: selectedMember,
        date: scheduleDate,
        start_time: scheduleStartTime,
        end_time: scheduleEndTime,
        job_id: scheduleJobId || null,
        notes: scheduleNotes
      });
      
      toast.success('Schedule entry added!');
      setShowScheduleEntry(false);
      setSelectedMember(null);
      setScheduleDate('');
      setScheduleNotes('');
      setScheduleJobId('');
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add schedule');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    // Stop typing indicator
    sendTypingIndicator(false);
    
    try {
      setSendingMessage(true);
      
      // If WebSocket is connected, send via WebSocket for real-time delivery
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'chat_message',
          content: newMessage
        }));
        setNewMessage('');
      } else {
        // Fallback to REST API
        await teamsAPI.sendMessage(team._id, { content: newMessage });
        setNewMessage('');
        
        // Refresh messages
        const msgRes = await teamsAPI.getMessages(team._id);
        setMessages(msgRes.data.messages || []);
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner': return <Crown size={16} weight="fill" className="text-yellow-500" />;
      case 'admin': return <Shield size={16} weight="fill" className="text-blue-500" />;
      case 'manager': return <Briefcase size={16} weight="fill" className="text-green-500" />;
      default: return <User size={16} weight="fill" className="text-neutral-500" />;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'owner': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'admin': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'manager': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      default: return 'bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-400';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
        <SpinnerGap size={48} weight="bold" className="text-[#2B4C6F] animate-spin" />
      </div>
    );
  }

  // Show pending invitations if user has any
  if (myInvitations.length > 0 && !team) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-3xl font-black text-neutral-900 dark:text-white mb-6">Team Invitations</h1>
          
          <div className="space-y-4">
            {myInvitations.map(inv => (
              <div key={inv._id} className="bg-white dark:bg-neutral-800 rounded-xl border-2 border-black dark:border-neutral-700 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-neutral-900 dark:text-white">{inv.team_name}</h3>
                    <p className="text-neutral-600 dark:text-neutral-400">Invited by {inv.invited_by_name}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-bold ${getRoleBadgeColor(inv.role)}`}>
                    {inv.role}
                  </span>
                </div>
                
                {inv.message && (
                  <p className="text-neutral-700 dark:text-neutral-300 mb-4 italic">"{inv.message}"</p>
                )}
                
                <div className="flex gap-3">
                  <Button
                    onClick={() => handleAcceptInvitation(inv._id)}
                    className="flex-1 bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold border-2 border-black shadow-[3px_3px_0px_#000]"
                  >
                    <Check size={18} weight="bold" className="mr-2" />
                    Accept
                  </Button>
                  <Button
                    onClick={() => handleDeclineInvitation(inv._id)}
                    variant="outline"
                    className="flex-1 border-2 border-black dark:border-neutral-600 font-bold"
                  >
                    Decline
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // No team - show create team prompt
  if (!team) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-white dark:bg-neutral-800 rounded-xl border-2 border-black dark:border-neutral-700 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] p-8">
            <Users size={64} weight="bold" className="text-[#2B4C6F] mx-auto mb-4" />
            <h1 className="text-3xl font-black text-neutral-900 dark:text-white mb-2">Team Management</h1>
            <p className="text-neutral-600 dark:text-neutral-400 mb-6">
              Create a team to manage your crew, assign jobs, and communicate effectively.
            </p>
            
            <Button
              onClick={() => setShowCreateTeam(true)}
              className="bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold text-lg px-8 py-3 h-auto border-2 border-black shadow-[4px_4px_0px_#000]"
            >
              <Plus size={20} weight="bold" className="mr-2" />
              Create Your Team
            </Button>
          </div>
        </div>
        
        {/* Create Team Dialog */}
        <Dialog open={showCreateTeam} onOpenChange={setShowCreateTeam}>
          <DialogContent className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700">
            <DialogHeader>
              <DialogTitle className="text-xl font-black text-neutral-900 dark:text-white">Create Team</DialogTitle>
              <DialogDescription>Set up your team to start managing crew members.</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-bold text-neutral-700 dark:text-neutral-300 mb-1">Team Name *</label>
                <Input
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  placeholder="e.g., ABC Construction Crew"
                  className="border-2 border-neutral-300 dark:border-neutral-600"
                />
              </div>
              
              <div>
                <label className="block text-sm font-bold text-neutral-700 dark:text-neutral-300 mb-1">Description</label>
                <Input
                  value={newTeamDesc}
                  onChange={(e) => setNewTeamDesc(e.target.value)}
                  placeholder="Brief description of your team"
                  className="border-2 border-neutral-300 dark:border-neutral-600"
                />
              </div>
              
              <Button
                onClick={handleCreateTeam}
                className="w-full bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold border-2 border-black shadow-[3px_3px_0px_#000]"
              >
                Create Team
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Team exists - show full management UI
  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white dark:bg-neutral-800 rounded-xl border-2 border-black dark:border-neutral-700 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-black text-neutral-900 dark:text-white flex items-center gap-3">
                <Users size={32} weight="bold" className="text-[#2B4C6F]" />
                {team.name}
              </h1>
              {team.description && (
                <p className="text-neutral-600 dark:text-neutral-400 mt-1">{team.description}</p>
              )}
            </div>
            
            {(team.my_role === 'owner' || team.my_role === 'admin' || team.my_role === 'manager') && (
              <Button
                onClick={() => setShowInviteMember(true)}
                className="bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold border-2 border-black shadow-[3px_3px_0px_#000]"
              >
                <UserPlus size={18} weight="bold" className="mr-2" />
                Invite Member
              </Button>
            )}
          </div>
          
          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-lg p-4 text-center">
                <div className="text-2xl font-black text-[#2B4C6F]">{stats.member_count}</div>
                <div className="text-sm font-bold text-neutral-600 dark:text-neutral-400">Members</div>
              </div>
              <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-lg p-4 text-center">
                <div className="text-2xl font-black text-green-600">{stats.active_assignments}</div>
                <div className="text-sm font-bold text-neutral-600 dark:text-neutral-400">Assignments</div>
              </div>
              <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-lg p-4 text-center">
                <div className="text-2xl font-black text-blue-600">{stats.open_jobs}</div>
                <div className="text-sm font-bold text-neutral-600 dark:text-neutral-400">Open Jobs</div>
              </div>
              <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-lg p-4 text-center">
                <div className="text-2xl font-black text-orange-600">{stats.pending_invitations}</div>
                <div className="text-sm font-bold text-neutral-600 dark:text-neutral-400">Pending Invites</div>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'members', label: 'Members', icon: Users },
            { id: 'assignments', label: 'Assignments', icon: Briefcase },
            { id: 'schedule', label: 'Schedule', icon: Calendar },
            { id: 'chat', label: 'Team Chat', icon: ChatCircle },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-[#2B4C6F] text-white border-2 border-black shadow-[2px_2px_0px_#000]'
                  : 'bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 border-2 border-neutral-200 dark:border-neutral-700 hover:border-[#2B4C6F]'
              }`}
            >
              <tab.icon size={18} weight="bold" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white dark:bg-neutral-800 rounded-xl border-2 border-black dark:border-neutral-700 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] p-6">
          
          {/* Members Tab */}
          {activeTab === 'members' && (
            <div>
              <h2 className="text-xl font-black text-neutral-900 dark:text-white mb-4">Team Members</h2>
              
              <div className="space-y-3">
                {members.map(member => (
                  <div key={member._id} className="flex items-center justify-between p-4 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg border-2 border-neutral-200 dark:border-neutral-600">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#2B4C6F]/20 flex items-center justify-center">
                        <span className="text-lg font-black text-[#2B4C6F]">
                          {member.name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-neutral-900 dark:text-white">{member.name}</span>
                          {getRoleIcon(member.role)}
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${getRoleBadgeColor(member.role)}`}>
                            {member.role}
                          </span>
                        </div>
                        <div className="text-sm text-neutral-600 dark:text-neutral-400">{member.email}</div>
                        <div className="text-xs text-neutral-500 mt-1">
                          {member.active_assignments} active assignment{member.active_assignments !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                    
                    {(team.my_role === 'owner' || team.my_role === 'admin') && member.role !== 'owner' && (
                      <Button
                        onClick={() => handleRemoveMember(member.user_id, member.name)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash size={18} weight="bold" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Assignments Tab */}
          {activeTab === 'assignments' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-black text-neutral-900 dark:text-white">Job Assignments</h2>
                {(team.my_role === 'owner' || team.my_role === 'admin' || team.my_role === 'manager') && (
                  <Button
                    onClick={() => setShowAssignJob(true)}
                    className="bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold border-2 border-black shadow-[2px_2px_0px_#000]"
                    size="sm"
                  >
                    <Plus size={16} weight="bold" className="mr-1" />
                    Assign Job
                  </Button>
                )}
              </div>
              
              {assignments.length === 0 ? (
                <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
                  <Briefcase size={48} weight="light" className="mx-auto mb-2 opacity-50" />
                  <p>No job assignments yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {assignments.map(assignment => (
                    <div key={assignment._id} className="flex items-center justify-between p-4 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg border-2 border-neutral-200 dark:border-neutral-600">
                      <div>
                        <div className="font-bold text-neutral-900 dark:text-white">{assignment.job_title}</div>
                        <div className="text-sm text-neutral-600 dark:text-neutral-400">
                          Assigned to: <span className="font-semibold">{assignment.member_name}</span>
                        </div>
                        {assignment.notes && (
                          <div className="text-xs text-neutral-500 mt-1 italic">{assignment.notes}</div>
                        )}
                      </div>
                      
                      <Button
                        onClick={() => handleRemoveAssignment(assignment._id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                      >
                        <X size={18} weight="bold" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Schedule Tab */}
          {activeTab === 'schedule' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-black text-neutral-900 dark:text-white">Team Schedule</h2>
                {(team.my_role === 'owner' || team.my_role === 'admin' || team.my_role === 'manager') && (
                  <Button
                    onClick={() => setShowScheduleEntry(true)}
                    className="bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold border-2 border-black shadow-[2px_2px_0px_#000]"
                    size="sm"
                  >
                    <Plus size={16} weight="bold" className="mr-1" />
                    Add Entry
                  </Button>
                )}
              </div>
              
              {schedule.length === 0 ? (
                <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
                  <Calendar size={48} weight="light" className="mx-auto mb-2 opacity-50" />
                  <p>No schedule entries yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {schedule.map(entry => (
                    <div key={entry._id} className="flex items-center justify-between p-4 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg border-2 border-neutral-200 dark:border-neutral-600">
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <div className="text-lg font-black text-[#2B4C6F]">{new Date(entry.date).getDate()}</div>
                          <div className="text-xs text-neutral-500">{new Date(entry.date).toLocaleDateString('en-US', { month: 'short' })}</div>
                        </div>
                        <div>
                          <div className="font-bold text-neutral-900 dark:text-white">{entry.member_name}</div>
                          <div className="text-sm text-neutral-600 dark:text-neutral-400 flex items-center gap-2">
                            <Clock size={14} />
                            {entry.start_time} - {entry.end_time}
                          </div>
                          {entry.job_title && (
                            <div className="text-xs text-[#2B4C6F] mt-1 flex items-center gap-1">
                              <Briefcase size={12} />
                              {entry.job_title}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Chat Tab */}
          {activeTab === 'chat' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-black text-neutral-900 dark:text-white flex items-center gap-2">
                  Team Chat
                  {wsConnected && (
                    <span className="flex items-center gap-1 text-xs font-normal text-green-600 dark:text-green-400">
                      <Circle size={8} weight="fill" className="animate-pulse" />
                      Live
                    </span>
                  )}
                </h2>
                {onlineMembers.length > 0 && (
                  <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                    <Circle size={8} weight="fill" className="text-green-500" />
                    {onlineMembers.length} online
                  </div>
                )}
              </div>
              
              <div 
                ref={chatContainerRef}
                className="h-80 overflow-y-auto mb-4 p-4 bg-neutral-50 dark:bg-neutral-700/30 rounded-lg border-2 border-neutral-200 dark:border-neutral-600"
              >
                {messages.length === 0 ? (
                  <div className="text-center py-8 text-neutral-500">
                    <ChatCircle size={48} weight="light" className="mx-auto mb-2 opacity-50" />
                    <p>No messages yet. Start the conversation!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {messages.map(msg => (
                      <div key={msg._id} className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#2B4C6F]/20 flex items-center justify-center flex-shrink-0 relative">
                          <span className="text-sm font-bold text-[#2B4C6F]">
                            {msg.sender_name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                          {onlineMembers.includes(msg.sender_id) && (
                            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-neutral-800"></div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-neutral-900 dark:text-white text-sm">{msg.sender_name}</span>
                            <span className="text-xs text-neutral-500">
                              {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          <p className="text-neutral-700 dark:text-neutral-300 text-sm">{msg.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Typing indicator */}
              {typingUsers.length > 0 && (
                <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-2 flex items-center gap-2">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-1.5 h-1.5 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-1.5 h-1.5 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                  {typingUsers.map(u => u.user_name).join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...
                </div>
              )}
              
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={handleMessageInputChange}
                  placeholder="Type a message..."
                  className="flex-1 border-2 border-neutral-300 dark:border-neutral-600"
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={sendingMessage || !newMessage.trim()}
                  className="bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold border-2 border-black shadow-[2px_2px_0px_#000]"
                >
                  {sendingMessage ? <SpinnerGap size={18} className="animate-spin" /> : <ArrowRight size={18} weight="bold" />}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Invite Member Dialog */}
      <Dialog open={showInviteMember} onOpenChange={setShowInviteMember}>
        <DialogContent className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700">
          <DialogHeader>
            <DialogTitle className="text-xl font-black text-neutral-900 dark:text-white">Invite Team Member</DialogTitle>
            <DialogDescription>Send an invitation to join your team.</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-bold text-neutral-700 dark:text-neutral-300 mb-1">Email Address *</label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="member@example.com"
                className="border-2 border-neutral-300 dark:border-neutral-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-bold text-neutral-700 dark:text-neutral-300 mb-1">Role</label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger className="border-2 border-neutral-300 dark:border-neutral-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-bold text-neutral-700 dark:text-neutral-300 mb-1">Personal Message</label>
              <Input
                value={inviteMessage}
                onChange={(e) => setInviteMessage(e.target.value)}
                placeholder="Optional message to include with invitation"
                className="border-2 border-neutral-300 dark:border-neutral-600"
              />
            </div>
            
            <Button
              onClick={handleInviteMember}
              className="w-full bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold border-2 border-black shadow-[3px_3px_0px_#000]"
            >
              <EnvelopeSimple size={18} weight="bold" className="mr-2" />
              Send Invitation
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Job Dialog */}
      <Dialog open={showAssignJob} onOpenChange={setShowAssignJob}>
        <DialogContent className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700">
          <DialogHeader>
            <DialogTitle className="text-xl font-black text-neutral-900 dark:text-white">Assign Job</DialogTitle>
            <DialogDescription>Assign a team member to a job.</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-bold text-neutral-700 dark:text-neutral-300 mb-1">Team Member *</label>
              <Select value={selectedMember || ''} onValueChange={setSelectedMember}>
                <SelectTrigger className="border-2 border-neutral-300 dark:border-neutral-600">
                  <SelectValue placeholder="Select member" />
                </SelectTrigger>
                <SelectContent>
                  {members.filter(m => m.role !== 'owner').map(member => (
                    <SelectItem key={member.user_id} value={member.user_id}>
                      {member.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-bold text-neutral-700 dark:text-neutral-300 mb-1">Job *</label>
              <Select value={selectedJob || ''} onValueChange={setSelectedJob}>
                <SelectTrigger className="border-2 border-neutral-300 dark:border-neutral-600">
                  <SelectValue placeholder="Select job" />
                </SelectTrigger>
                <SelectContent>
                  {myJobs.map(job => (
                    <SelectItem key={job._id} value={job._id}>
                      {job.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-bold text-neutral-700 dark:text-neutral-300 mb-1">Notes</label>
              <Input
                value={assignmentNotes}
                onChange={(e) => setAssignmentNotes(e.target.value)}
                placeholder="e.g., Lead electrician on this project"
                className="border-2 border-neutral-300 dark:border-neutral-600"
              />
            </div>
            
            <Button
              onClick={handleAssignJob}
              className="w-full bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold border-2 border-black shadow-[3px_3px_0px_#000]"
            >
              Assign to Job
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule Entry Dialog */}
      <Dialog open={showScheduleEntry} onOpenChange={setShowScheduleEntry}>
        <DialogContent className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700">
          <DialogHeader>
            <DialogTitle className="text-xl font-black text-neutral-900 dark:text-white">Add Schedule Entry</DialogTitle>
            <DialogDescription>Schedule a team member's work shift.</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-bold text-neutral-700 dark:text-neutral-300 mb-1">Team Member *</label>
              <Select value={selectedMember || ''} onValueChange={setSelectedMember}>
                <SelectTrigger className="border-2 border-neutral-300 dark:border-neutral-600">
                  <SelectValue placeholder="Select member" />
                </SelectTrigger>
                <SelectContent>
                  {members.map(member => (
                    <SelectItem key={member.user_id} value={member.user_id}>
                      {member.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-bold text-neutral-700 dark:text-neutral-300 mb-1">Date *</label>
              <Input
                type="date"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                className="border-2 border-neutral-300 dark:border-neutral-600"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-neutral-700 dark:text-neutral-300 mb-1">Start Time</label>
                <Input
                  type="time"
                  value={scheduleStartTime}
                  onChange={(e) => setScheduleStartTime(e.target.value)}
                  className="border-2 border-neutral-300 dark:border-neutral-600"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-neutral-700 dark:text-neutral-300 mb-1">End Time</label>
                <Input
                  type="time"
                  value={scheduleEndTime}
                  onChange={(e) => setScheduleEndTime(e.target.value)}
                  className="border-2 border-neutral-300 dark:border-neutral-600"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-bold text-neutral-700 dark:text-neutral-300 mb-1">Related Job</label>
              <Select value={scheduleJobId} onValueChange={setScheduleJobId}>
                <SelectTrigger className="border-2 border-neutral-300 dark:border-neutral-600">
                  <SelectValue placeholder="None (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  {myJobs.map(job => (
                    <SelectItem key={job._id} value={job._id}>
                      {job.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-bold text-neutral-700 dark:text-neutral-300 mb-1">Notes</label>
              <Input
                value={scheduleNotes}
                onChange={(e) => setScheduleNotes(e.target.value)}
                placeholder="e.g., Morning shift at site A"
                className="border-2 border-neutral-300 dark:border-neutral-600"
              />
            </div>
            
            <Button
              onClick={handleAddScheduleEntry}
              className="w-full bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold border-2 border-black shadow-[3px_3px_0px_#000]"
            >
              Add Schedule Entry
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamManagement;
